import { AxiosError } from "axios";
import { useCallback, useState } from "react";
import { useSelector } from "react-redux";

import { APPLICATION_TYPE, PRODUCT_DOMAIN } from "@/constants";
import axioInstance from "@/libs/axios";
import { useLoading } from "@/providers/loading-provider";
import { RootState } from "@/store/root-reducer";
import { QuoteData } from "@/types/quote/quote-data";
import logError from "@/utils/logger";

interface UpdateProps {
  resetTrigger: () => void;
}

type QuoteResponse = {
  data: {
    data: QuoteData;
  };
};

// Custom hook for cancellation cover updating
export default function useUpdateCancellationCover({
  resetTrigger,
}: UpdateProps) {
  const [data, setData] = useState<null | QuoteData>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<null | AxiosError>(null);
  const [showErrorModal, setShowErrorModal] = useState(false);

  const { attributes, version, referenceId } = useSelector(
    (state: RootState) => state.quote
  );

  const { showLoading, hideLoading } = useLoading();

  const updateCancellationCover = useCallback(
    async (bodyData: unknown, tripDetailsBody: unknown) => {
      showLoading();

      const postBody = {
        data: {
          type: APPLICATION_TYPE,
          channel: "ONLINE",
          version,
          attributes: {
            domain: PRODUCT_DOMAIN,
            priceResult: bodyData,
            tripDetails: tripDetailsBody,
          },
        },
      };

      try {
        const response: QuoteResponse = await axioInstance.patch(
          `${process.env.NEXT_PUBLIC_BASE_URL}/quote/${referenceId}/cancellationCover`,
          postBody
        );

        const priceResult = response?.data?.data?.attributes?.priceResult;
        if (!priceResult) {
          setShowErrorModal(true);
        }
        setData(response.data.data);
      } catch (err) {
        const axiosError = err as AxiosError;
        logError(axiosError, { location: "Quote Cancellation Cover Change" });
        setError(axiosError);
      } finally {
        setLoading(false);
        hideLoading();
        resetTrigger();
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [attributes, version, referenceId, resetTrigger, showLoading, hideLoading]
  );

  return {
    data,
    loading,
    error,
    updateCancellationCover,
    showErrorModal,
    setShowErrorModal,
  };
}
